import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { contactInfo } from "../data/mock";
import { toast } from "sonner";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    phone: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build mailto link with form data
    const subject = `Contact from ${formData.name}`;
    const body = `Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone || 'Not provided'}

Message:
${formData.message}`;
    
    const mailtoLink = `mailto:contact@smxmedical.co.uk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Clear form after opening email client
    setTimeout(() => {
      setFormData({
        name: "",
        email: "",
        message: "",
        phone: "",
      });
      toast.success("Opening your email app...");
    }, 500);
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contact SMX Medical
          </h2>
          <p className="text-xl text-gray-600">
            Get in touch for medical cover enquiries or event support. We will gladly answer any question you may have.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Contact Information
              </h3>
              <div className="space-y-6">
                {/* Call */}
                <Card className="border-2 border-[#0056c5]/20 hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#0056c5]/10 rounded-lg flex items-center justify-center">
                        <Phone className="h-6 w-6 text-[#0056c5]" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-bold">Call</CardTitle>
                        <a
                          href={`tel:${contactInfo.phone.replace(/\s/g, "")}`}
                          className="text-[#0056c5] font-semibold hover:underline text-sm"
                        >
                          {contactInfo.phone}
                        </a>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                {/* Email */}
                <Card className="border-2 border-[#0056c5]/20 hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#0056c5]/10 rounded-lg flex items-center justify-center">
                        <Mail className="h-6 w-6 text-[#0056c5]" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-bold">Email</CardTitle>
                        <a
                          href={`mailto:${contactInfo.email}`}
                          className="text-[#0056c5] font-semibold hover:underline break-all text-sm"
                        >
                          {contactInfo.email}
                        </a>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                {/* Service Area */}
                <Card className="border-2 border-[#0056c5]/20 hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#0056c5]/10 rounded-lg flex items-center justify-center">
                        <MapPin className="h-6 w-6 text-[#0056c5]" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-bold">Service Area</CardTitle>
                        <p className="text-gray-600 text-sm">Surrey, Sussex & Beyond</p>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </div>
            </div>
          </div>

          {/* Contact Form - Matching original website structure */}
          <div>
            <Card className="border-2 border-[#0056c5]/20 shadow-xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Field */}
                  <div className="space-y-2">
                    <Label htmlFor="Name" className="text-sm font-medium text-gray-900">
                      Name*
                    </Label>
                    <Input
                      id="Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter your name"
                      required
                      className="border-gray-300 focus:border-[#0056c5] focus:ring-[#0056c5] h-12"
                    />
                  </div>

                  {/* Email Field */}
                  <div className="space-y-2">
                    <Label htmlFor="Email" className="text-sm font-medium text-gray-900">
                      Your Email Address*
                    </Label>
                    <Input
                      id="Email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter your email address"
                      required
                      className="border-gray-300 focus:border-[#0056c5] focus:ring-[#0056c5] h-12"
                    />
                  </div>

                  {/* Message Field */}
                  <div className="space-y-2">
                    <Label htmlFor="Message" className="text-sm font-medium text-gray-900">
                      Your Message*
                    </Label>
                    <Textarea
                      id="Message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Type your message here"
                      required
                      rows={6}
                      className="border-gray-300 focus:border-[#0056c5] focus:ring-[#0056c5] resize-none"
                    />
                  </div>

                  {/* Phone Field (Optional) */}
                  <div className="space-y-2">
                    <Label htmlFor="Telephone" className="text-sm font-medium text-gray-900">
                      Telephone Number (optional)
                    </Label>
                    <Input
                      id="Telephone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Please enter your phone number"
                      className="border-gray-300 focus:border-[#0056c5] focus:ring-[#0056c5] h-12"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full bg-[#0056c5] hover:bg-[#004399] text-white py-6 text-lg font-semibold rounded-[50px] group"
                  >
                    Send via Email
                    <Send className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
